import React, { FC, Fragment, useState, useEffect, HTMLProps } from 'react'

/** @jsx jsx */
import { jsx, css } from '@emotion/core'
import { ChevronLeftIcon } from '@components/Icons'
import { formInputErrorStyle, formInputPlaceholderStyle } from '@css/styles'
import { SelectorContainer, arrowIconStyles } from './Selector.elements'

export const DATA_TYPE_INTEGER = 'integer'

type SelectorOption = {
  text: string
  value: string
}

type Props = {
  id?: string
  name: string
  onChange: Function
  value: string
  dataType?: string
  placeholder?: string
  error?: boolean
  options: Array<SelectorOption>
  arrowVariant?: 'blue' | 'lightBlue'
  variant?: 'default' | 'simple'
  disabled?: boolean
}

export const Selector: FC<Props> = ({
  name,
  onChange,
  value,
  dataType,
  placeholder,
  error,
  options,
  arrowVariant = 'blue',
  variant = 'default',
  ...props
}) => {
  const [, setIsClient] = useState<boolean>(false)
  useEffect(() => {
    // This is required to trigger a two-pass rendering.
    // When this component is rendered at server side, it won't have the right
    //  values selected. As a result, the `hydrate` process will take the server
    //  side HTML as valid. So we trigger a new render to use the new values on client side.
    // (https://en.reactjs.org/docs/react-dom.html#hydrate)
    setIsClient(true)
  })

  const handleChange = event => {
    let updatedValue = event.target.value
    if (dataType === DATA_TYPE_INTEGER) {
      updatedValue = parseInt(updatedValue, 10)
    }

    if ((updatedValue + '') === (placeholder + '')) {
      updatedValue = null
    }

    onChange(name, updatedValue)
  }

  let styles: any = []
  if (typeof(value) === 'undefined' || value === '' || value === placeholder) {
    styles.push(formInputPlaceholderStyle)
  }
  if (error) {
    styles.push(formInputErrorStyle)
  }

  const placeholderOption = placeholder && <option disabled hidden value="">{placeholder}</option>

  return (
    <Fragment>
      <SelectorContainer
        {...props}
        name={name}
        onChange={handleChange}
        css={styles}
        value={value}
        arrowVariant={arrowVariant}
        variant={variant}
      >
        {placeholderOption}
        {options.map((option, i) => {
            const id = `${name}-${i}`
            return (
              <option
                id={id}
                key={id}
                value={option.value}
                label={option.text || option.value}
              >
                {option.text}
              </option>
            )
          })
        }
      </SelectorContainer>

      {variant === 'simple' && (
        <ChevronLeftIcon css={arrowIconStyles} />
      )}
    </Fragment>
  )
}
